/**
 * A DOCUMENT PATH, READ AS A TABLE AND A ROW.
 *
 * The app asks for `projects/main/issues/i1`. Postgres has an `issues` table
 * with a `board_id`. Something has to translate, and this is it — kept apart
 * from the Supabase client so the mapping can be argued about, and TESTED,
 * without a database anywhere near it.
 *
 * WHY THE APP WAS NOT CHANGED TO SPEAK SQL INSTEAD. That is the better end
 * state and it is the wrong first move: it would mean rewriting the store at
 * the same moment as changing the database, so a fault afterwards could be
 * either, and the 41 suites that currently prove the app would all have to be
 * re-argued. One change at a time. The store is normalised AFTER Postgres is
 * the authority, with the tests holding it still.
 *
 * THE THREE PLACES THE SHAPES GENUINELY DISAGREE, named here rather than
 * discovered later:
 *
 *   access/allowlist   one document holding every email and every admin, which
 *                      on Postgres is the `people` table read sideways.
 *   roles/{email}      a document holding one role, which is a COLUMN on
 *                      people — while roles/{email}/on/{board} is a row in a
 *                      table of its own.
 *   an issue's         arrays inside the document, which are rows in `comments`
 *   comments           and `activity` here. Nested on the way out so the app
 *                      still sees what it expects.
 */

/** What a path turns into. */
export type Target =
  | { kind: 'row'; table: string; where: Record<string, unknown> }
  | { kind: 'rows'; table: string; where: Record<string, unknown> }
  /** The shapes with no table of their own — see the note above. */
  | { kind: 'access' }
  | { kind: 'role'; email: string }
  | { kind: 'roles' }
  | { kind: 'profile'; email: string }
  | { kind: 'profiles' }
  | { kind: 'boardRole'; email: string; board: string }
  | { kind: 'boardRoles' };

const parts = (path: string) => path.split('/').filter(Boolean);

/**
 * TRANSLATING AN ID, AND WHY IT IS A HASH.
 *
 * Firestore names documents with whatever string the app invented — 'main',
 * 'sandbox', or the eight characters `uid()` produces. Postgres wants a UUID.
 * The same hash the migration used means an id survives the move: the board
 * the app called 'main' is the same row on both sides, so a half-migrated
 * browser holding an old id in local storage still finds its board.
 *
 * Anything already shaped like a UUID is passed through, because after the
 * switch every id the app holds came FROM Postgres.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function idFrom(kind: string, key: string, md5: (s: string) => string): string {
  if (UUID.test(key)) return key.toLowerCase();
  const h = md5(`${kind}:${key}`);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-8${h.slice(17, 20)}-${h.slice(20, 32)}`;
}

/**
 * Where a path points.
 *
 * An odd number of segments is a collection and an even number is a document —
 * Firestore's rule, kept because every caller already obeys it and because it
 * reads well.
 */
/**
 * THE NUMBER IN A BUILD PATH, REFUSED RATHER THAN GUESSED.
 *
 * `Number('b8')` is NaN, which JSON turns into `null`, which arrives at Postgres
 * as a row with no build number — so the write is refused and the caller is told
 * nothing useful about why. That is exactly what happened when the release
 * screen addressed a build by its LABEL: `id` on a Build is "b102", a label the
 * app shows, and the key is the number 102. Cutting a release wrote to
 * `builds/b8` and the button sat on "Recording…" for ever.
 *
 * A path that cannot name a build is a mistake in the caller, and it says so
 * here rather than travelling on as a null.
 */
function buildNumber(segment: string, path: string): number {
  const n = Number(segment);
  if (!Number.isInteger(n))
    throw new Error(
      `"${path}" addresses a build by "${segment}" — a build is addressed by its NUMBER, not its "b123" label. See src/backend/paths.ts.`
    );
  return n;
}

export function target(path: string, id: (kind: string, key: string) => string): Target {
  const p = parts(path);
  const [head, a, mid, b] = p;

  if (head === 'access') return { kind: 'access' };

  if (head === 'roles') {
    if (p.length === 1) return { kind: 'roles' };
    if (p.length === 2) return { kind: 'role', email: a.toLowerCase() };
    // roles/{email}/on/{board} — what they may do on ONE board.
    if (p.length === 3 && mid === 'on') return { kind: 'boardRoles' };
    if (p.length === 4 && mid === 'on') return { kind: 'boardRole', email: a.toLowerCase(), board: id('board', b) };
  }

  if (head === 'profiles')
    return p.length === 1 ? { kind: 'profiles' } : { kind: 'profile', email: a.toLowerCase() };

  if (head === 'projects') {
    if (p.length === 1) return { kind: 'rows', table: 'boards', where: {} };
    if (p.length === 2) return { kind: 'row', table: 'boards', where: { id: id('board', a) } };
    /*
     * NAMED, NOT DEFAULTED. This read "issues, else sprints, else builds", so
     * any collection nobody had thought of landed in `builds` — silently, and
     * with a shape builds would refuse. A path this file does not know about is
     * a mistake in the caller and says so, rather than travelling on as a write
     * to the wrong table.
     */
    const TABLES: Record<string, string> = {
      issues: 'issues',
      areas: 'areas',
      // The board's managed labels — a per-board, uuid-keyed registry like areas
      // (#462 follow-on); the card keeps the name, this holds its colour.
      labels: 'labels',
      issueTypes: 'issue_types',
      phases: 'phases',
      // The Plan view's two collections — #377. A phase's fluid steps, and its
      // append-only history. Both keyed like a phase: the app's short id hashed
      // in, a Postgres uuid passed through.
      phaseSteps: 'phase_steps',
      phaseEvents: 'phase_events',
      // The standalone milestone register the Plan writes — 0124 for the table,
      // milestone payments for bill_value/billable. Read for the portfolio via
      // readGroup; watched per-board here so the active Plan can add/edit/remove.
      milestones: 'milestones',
      // The project decision log — 0171, 3.19 Learning & Governance spine. Watched
      // per-board so the Decisions tab can log/decide/supersede; a first-class table
      // with its own RLS, like milestones.
      decisions: 'decisions',
      // The project RAID register — 0193, four first-class tables watched per-board
      // so each register can log/edit/remove, with their own RLS, like decisions.
      risks: 'risks',
      assumptions: 'assumptions',
      registerIssues: 'register_issues',
      dependencies: 'dependencies',
      sprints: 'sprints',
      builds: 'builds',
      signoffs: 'signoffs',
      documents: 'documents',
    };
    const table = TABLES[mid];
    if (!table) throw new Error(`"${path}" names no collection this app knows. See src/backend/paths.ts.`);
    const board = id('board', a);
    if (p.length === 3) return { kind: 'rows', table, where: { board_id: board } };
    /*
     * An area's id is a uuid from Postgres, exactly like a sprint's, and for
     * the same reason it must not be hashed on the way back in: hashing a uuid
     * that CAME from the database produces a different one, and the row reads
     * as missing rather than as an error.
     */
    // The per-board, uuid-keyed collections: an id that is already a uuid came
    // FROM Postgres and is passed through; the app's own short ids are hashed,
    // so the namespace must match between read and write, and each table has its
    // own — hashing under the wrong namespace resolves to a different row, which
    // reads as missing rather than as an error.
    const HASH_NS: Record<string, string> = {
      areas: 'area',
      labels: 'label',
      phases: 'phase',
      phase_steps: 'step',
      phase_events: 'phaseEvent',
      issue_types: 'issueType',
      milestones: 'milestone',
      decisions: 'decision',
      // RAID register — 0193. Ids are uuids (crypto.randomUUID), passed through by
      // the UUID branch; each table gets its own namespace so a short id would hash
      // under the right one, matching read and write.
      risks: 'risk',
      assumptions: 'assumption',
      register_issues: 'registerIssue',
      dependencies: 'dependency',
      sprints: 'sprint',
    };
    if (HASH_NS[table])
      return {
        kind: 'row',
        table,
        where: {
          id: UUID.test(b) ? b.toLowerCase() : id(HASH_NS[table], `${a}/${b}`),
          board_id: board,
        },
      };
    return {
      kind: 'row',
      table,
      /*
       * THE BOARD IS PART OF THE KEY IN BOTH CASES, and leaving it off an
       * issue was a real fault: `where` is what a write is built from as well
       * as what a read filters by, so creating an issue produced a row with no
       * board at all. It failed loudly — a not-null violation — but it failed
       * on every issue anybody raised.
       *
       * A build is identified by its NUMBER on its board rather than an id,
       * which is the one place the app's path and the table's key differ in
       * shape rather than merely in name.
       */
      where:
        table === 'issues' || table === 'signoffs' || table === 'documents'
          ? {
              /*
               * AN ID THAT IS ALREADY AN ID MUST BE LEFT ALONE.
               *
               * `idFrom` passes a uuid straight through — but the key handed to
               * it here is `${a}/${b}`, which is never a uuid, so a uuid arriving
               * as `b` got hashed into a DIFFERENT one. The composed key is right
               * only for the app's own short ids, which is what the migration
               * hashed and what a board created in the app still uses.
               *
               * After the migration every issue the app holds came FROM Postgres,
               * so `b` is a uuid — and every single-issue write went to an id
               * that does not exist. Reading a LIST still worked, because that
               * asks by board rather than by issue, so the board looked perfectly
               * healthy while nothing on it could be changed:
               *
               *   a comment  refused, because it points at a missing issue
               *   an edit    silently nothing — an UPDATE matching no row is
               *              not an error, it is a success that did nothing
               *   a deletion the same
               *
               * That is the worst of the family found this week, because it does
               * not even fail. It just quietly declines to save anything.
               */
              id: UUID.test(b) ? b.toLowerCase() : id('issue', `${a}/${b}`),
              board_id: board,
            }
          : { board_id: board, build: buildNumber(b, path) },
    };
  }

  if (head === 'groups')
    return p.length === 1
      ? { kind: 'rows', table: 'groups', where: {} }
      : { kind: 'row', table: 'groups', where: { id: id('group', a) } };

  if (head === 'audit')
    return p.length === 1
      ? { kind: 'rows', table: 'audit', where: {} }
      : { kind: 'row', table: 'audit', where: { id: id('audit', a) } };

  /*
   * A REQUEST TO ADD SOMEBODY — #359. Tenant-scoped, not board-scoped: an admin
   * sees the whole workspace's pending requests in one queue, and RLS decides
   * whose each person may read (their own, or everybody's if they administer).
   * Its id is a uuid straight from Postgres, so it is passed through rather than
   * hashed — the same rule as a sprint's.
   */
  if (head === 'boardRequests')
    return p.length === 1
      ? { kind: 'rows', table: 'board_requests', where: {} }
      : { kind: 'row', table: 'board_requests', where: { id: a.toLowerCase() } };

  /*
   * A PERSON'S CALL-OUTS — track A, #388. Tenant-scoped, not board-scoped: the
   * bell gathers every board the person is on, and RLS narrows the list to the
   * rows they own (see notifications_own_read, 0104). Its id is a uuid straight
   * from Postgres, so it is passed through rather than hashed — the same rule as
   * a board request's. The app only ever writes `read_at`; the rows themselves
   * are minted by the database (0105).
   */
  if (head === 'notifications')
    return p.length === 1
      ? { kind: 'rows', table: 'notifications', where: {} }
      : { kind: 'row', table: 'notifications', where: { id: a.toLowerCase() } };

  /*
   * LIGHT REACTIONS ON A FEED ENTRY — migration 0143. Tenant-scoped like
   * notifications: ONE listener for the whole workspace gathers every board the
   * person is on, and RLS narrows the read to reactions on boards they can open
   * (reactions_read). The app merges them onto the feed by the entry id they
   * point at. A reaction has no id of its own — it is keyed by its natural tuple
   * — so it is never addressed as a single row here; it is read as a collection
   * and written through the backend's addReaction/removeReaction, the same way a
   * comment edit has its own method rather than a row path.
   */
  if (head === 'reactions') return { kind: 'rows', table: 'reactions', where: {} };

  /*
   * THE LESSONS REGISTER — migration 0172, 3.19 Learning & Governance spine (L1).
   * Tenant-scoped like reactions: one listener for the whole workspace, and RLS
   * (lessons_read) hands back workspace-wide lessons plus board lessons on boards the
   * person can open. Unlike a reaction a lesson has its own id, so it is addressed as
   * a single row to edit/remove; the app's short id is hashed under 'lesson', a uuid
   * from Postgres passed through — the same rule as a group's.
   */
  if (head === 'lessons')
    return p.length === 1
      ? { kind: 'rows', table: 'lessons', where: {} }
      : { kind: 'row', table: 'lessons', where: { id: UUID.test(a) ? a.toLowerCase() : id('lesson', a) } };

  /*
   * A SIGN-IN'S NETWORK ORIGIN — migration 0150. Tenant-scoped like reactions:
   * one listener for the whole workspace, and RLS (audit_origin_read) returns
   * rows only to an admin — nothing to anyone else, not even the person a row is
   * about. The app merges each onto its sign-in line by the audit id it points
   * at. No id of its own — keyed by (tenant, audit_id), so it is read as rows and
   * never addressed as a single document.
   */
  if (head === 'auditOrigin') return { kind: 'rows', table: 'audit_origin', where: {} };

  /*
   * WHO COVERS WHOM WHILE A SIGNER IS AWAY — configurable sign-offs R1, migration
   * 0130. Tenant-scoped, not board-scoped: a cover applies across every board the
   * covered person is on, so the bell-less read gathers the whole workspace and
   * RLS keeps it to the tenant (signoff_cover_read). Its id is a uuid straight
   * from Postgres, passed through rather than hashed — the same rule as a
   * notification's. The seam adds tenant_id on write; the guard trigger fills
   * set_by / set_by_admin from the caller.
   */
  if (head === 'signoffCover')
    return p.length === 1
      ? { kind: 'rows', table: 'signoff_cover', where: {} }
      : { kind: 'row', table: 'signoff_cover', where: { id: a.toLowerCase() } };

  if (head === 'sessions')
    return p.length === 1
      ? { kind: 'rows', table: 'sessions', where: {} }
      : // A session is keyed on the device's own id, which is already a string
        // the browser generated and is stored as one.
        { kind: 'row', table: 'sessions', where: { id: a } };

  /*
   * THE WORKSPACE ROW ITSELF — its currency, read on load and written by an
   * admin (0117). Keyed on the tenant id from the sign-in token, which is a uuid
   * straight from Postgres, so it is passed through rather than hashed — the same
   * rule as a board request's. RLS lets any member read their own tenant and only
   * an admin change its currency; the column privilege limits the write to that
   * one column, so nothing here can touch the name, address or suspension.
   */
  if (head === 'workspace') return { kind: 'row', table: 'tenants', where: { id: a.toLowerCase() } };

  throw new Error(`No table for the path "${path}" — see src/backend/paths.ts`);
}

/**
 * The app's field names, and the columns they are.
 *
 * Two vocabularies rather than one because renaming every field in the app at
 * the same time as changing the database is two changes wearing one hat. The
 * app keeps camelCase; Postgres keeps its own convention; this is the seam.
 */
export const COLUMNS: Record<string, string> = {
  // MISSING AT FIRST, and it read as an empty document rather than an error:
  // `roles/{email}` came back as {} because the app asks for `defaultRole` and
  // the column is `default_role`, so the lookup found nothing and said nothing.
  // Every person would have been a viewer.
  defaultRole: 'default_role',
  isAdmin: 'is_admin',
  // A request to add somebody — #359.
  boardId: 'board_id',
  requestedBy: 'requested_by',
  displayName: 'display_name',
  decidedBy: 'decided_by',
  decidedAt: 'decided_at',
  declineReason: 'decline_reason',
  /* The decision log — 0171, 3.19 spine. Multi-word fields that differ from the
     column; `decidedBy`/`decidedAt`/`createdAt`/`updatedAt`/`boardId` are already
     mapped above, and status/title/detail/origin/links/supersedes are the same word
     either side. decided_at/created_at/updated_at are timestamptz, already in TIMES. */
  madeBy: 'made_by',
  originRef: 'origin_ref',
  /* The RAID register — 0193. Multi-word fields that differ from the column
     (boardId/madeBy/createdAt/updatedAt are mapped globally above; single-word ones
     — title, response, likelihood, impact, category, strategy, status, owner, num,
     position, links, note, direction, confidence, priority — are the same either
     side). reviewed_at/next_review_at/validate_by_at/raised_at/needed_by_at are
     timestamptz, so they are ALSO in TIMES below. */
  reviewedAt: 'reviewed_at',
  nextReviewAt: 'next_review_at',
  seededFromLessonId: 'seeded_from_lesson_id',
  impactIfFalse: 'impact_if_false',
  validateByAt: 'validate_by_at',
  convertedToRiskId: 'converted_to_risk_id',
  nextStep: 'next_step',
  raisedAt: 'raised_at',
  realisedFromRiskId: 'realised_from_risk_id',
  partyKind: 'party_kind',
  partyName: 'party_name',
  partyBoardId: 'party_board_id',
  neededByAt: 'needed_by_at',
  // Why a decided/open decision was withdrawn (0180). Plain text, so NOT in TIMES
  // or NUMBERS; two words, so it needs this entry or the PATCH would miss the column.
  withdrawnReason: 'withdrawn_reason',
  nextNumber: 'next_number',
  refPrefix: 'ref_prefix',
  refWidth: 'ref_width',
  sourceName: 'source_name',
  repoUrl: 'repo_url',
  /* A project's own definition — #387. description, priority, budget, sponsor,
     risks and stakeholders are the same word either side, so they need no entry
     (COLUMNS carries only the names that differ); the multi-word ones do. */
  startDate: 'start_date',
  goLive: 'go_live_at',
  /* How the go-live is anchored to the plan — 0217. A jsonb `{ kind, id? }` on `boards`;
     its own fields ride inside it, so it never passes through TIMES/NUMBERS. Two words,
     so the field→column name needs an entry. */
  goLiveAnchor: 'go_live_anchor',
  projectType: 'project_type',
  fundingSource: 'funding_source',
  successMeasure: 'success_measure',
  /* The agreed contract total the budget is based on — 0152. Two words, so it
     needs an entry; `contract_value` on `boards`. Numeric, so it is in NUMBERS
     below (read back as a number, like `budget`). */
  contractValue: 'contract_value',
  /* The log of approved contract-value changes — 0153 / #435. A jsonb list on
     `boards`, like `invoices`; the entries' own fields ride inside it and never
     pass through this map, so it is NOT in TIMES or NUMBERS. Two words, so the
     field→column name needs an entry. */
  contractAdjustments: 'contract_adjustments',
  /* The board's delivery-% nudge config — 0170 / #483. A jsonb `{ on, pct }` on
     `boards`; the shape's own fields ride inside it and never pass through this
     map, so it is NOT in TIMES or NUMBERS. The field→column name needs an entry. */
  deliveryNudge: 'delivery_nudge',
  /* Run as a project or a continuous service — 0151 / #435. Two words, so it
     needs an entry; the column is `delivery_mode` on `boards`. A text enum, not
     a timestamptz, so it is NOT in TIMES. */
  deliveryMode: 'delivery_mode',
  /* How the vendor(s) are paid — fixed-price / time-and-materials / mixed — 0219.
     A text enum on `boards`, the project-level cost basis that tunes how the cost
     tile reads CPI vs the forecast verdict. Two words → needs an entry; not a time
     or amount, so NOT in TIMES or NUMBERS. */
  costBasis: 'cost_basis',
  /* How a new card auto-assigns its creator — 0195 / two-part assignee (A). A
     text enum on `boards`, the board-wide override of the per-person company-role
     default. Two words → needs an entry; not a time or amount. */
  cardDefaults: 'card_defaults',
  /* The two per-person workspace attributes that drive the adaptive card default
     — 0195. Columns on `people`, carried as Profile fields; multi-word → entries. */
  companyRole: 'company_role',
  cardDefaultStance: 'card_default_stance',
  /* The optional In Staging column — 0181 / #471. A boolean on `boards`; needs
     an entry because the field is multi-word. A plain boolean, not a timestamptz,
     so it is NOT in TIMES. */
  stagingEnabled: 'staging_enabled',
  /* Hide the Done column while In Staging is on — 0183. A companion boolean on
     `boards`; needs an entry because the field is multi-word. A plain boolean,
     not a timestamptz, so it is NOT in TIMES. */
  hideDoneInStaging: 'hide_done_in_staging',
  /* Show the backlog as a column on the board — 0218. A per-board boolean on
     `boards`; needs an entry because the field is multi-word. A plain boolean,
     not a timestamptz, so it is NOT in TIMES. */
  backlogOnBoard: 'backlog_on_board',
  /* Show the completion odds to everyone, not only a PM-and-above — 0220. A per-board
     boolean on `boards`; needs an entry because the field is multi-word. A plain boolean,
     not a timestamptz, so it is NOT in TIMES. */
  showOddsToEveryone: 'show_odds_to_everyone',
  /* Delivery-signal audiences — 0225. Per-board 'off'|'managers'|'all' text on `boards`;
     multi-word, so each needs an entry. Plain text, not timestamptz, so NOT in TIMES. */
  scheduleSignalAudience: 'schedule_signal_audience',
  budgetSignalAudience: 'budget_signal_audience',
  /* Superseded 0222 booleans, still mapped so a not-yet-migrated board reads them. */
  deliverySignalEnabled: 'delivery_signal_enabled',
  costInSignal: 'cost_in_signal',
  /* The go-live -> in-service handoff — 0192 / #435 surface 2. The real go-live
     moment, frozen at the handoff; two words, so the field→column name needs an
     entry, and it IS a timestamptz, so it is ALSO in TIMES below. */
  actualGoLive: 'actual_go_live_at',
  /* The as-delivered record frozen at the handoff — 0192 / #435 surface 2. A jsonb
     `{ plannedGoLive, actualGoLive, scopeShipped, scopeDeferred, budgetAtGoLive, … }`
     on `boards`, like `delivery_nudge`; the shape's own fields ride inside it and
     never pass through this map, so it is NOT in TIMES or NUMBERS. The field→column
     name needs an entry because it is multi-word. */
  deliverySnapshot: 'delivery_snapshot',
  /* Strict separation of duties for scope changes — 0154 / #435 A. A boolean on
     `boards`; needs an entry because the field is multi-word. */
  requireSeparateScopeApprover: 'require_separate_scope_approver',
  /* Hard finish-to-start dependency block — 0202. A boolean on `boards`; needs an
     entry because the field is multi-word. A plain boolean, not a timestamptz, so
     it is NOT in TIMES. */
  depHardBlock: 'dep_hard_block',
  /* The board's adaptive assists — the card size/date assists and the Insights
     Data quality panel (0205). Two plain booleans on `boards`, default on; each
     needs an entry because the field is multi-word. Not timestamptz, so NOT in
     TIMES. */
  cardAssists: 'card_assists',
  dataQualityPanel: 'data_quality_panel',
  /* Per-board idle-nudge windows — 0213. A nullable jsonb `{ bySize: { s, m, l },
     noSize }` on `boards` (null = use the app defaults); needs an entry because the
     field is multi-word. Its own fields ride inside the jsonb, like delivery_snapshot,
     so it is NOT in TIMES or NUMBERS. */
  idleWindows: 'idle_windows',
  /* Per-board checklist prompts — 0214. A nullable jsonb `{ on, onFirstTick,
     onComplete }` on `boards` (null = use the app defaults, everything on); needs an
     entry because the field is multi-word. Its own fields ride inside the jsonb, like
     idle_windows, so it is NOT in TIMES or NUMBERS. */
  checklistPrompts: 'checklist_prompts',
  /* Budget classification & tracking — 0108. `capex`, `opex` and `forecast` are
     the same word either side; the multi-word ones need an entry. */
  capexNote: 'capex_note',
  opexNote: 'opex_note',
  actualSpend: 'actual_spend',
  /* Retention rate — invoice-acceptance governance. A whole-number percent on the
     board; the ledger's lifecycle/records ride inside the `invoices` jsonb and
     need no column of their own. */
  retentionRate: 'retention_rate',
  /* Workspace exchange rates — 0118. The tenant's `fx_rates` jsonb, written from
     the seam PATCH on `workspace/{id}` alongside `currency`. */
  fxRates: 'fx_rates',
  /* The workspace payment-approval chain — 0182. The tenant's `payment_approval`
     jsonb ({default, overrides}), written from the same `workspace/{id}` seam. Its
     stages' own fields ride inside the jsonb and never pass through this map, so it
     is NOT in TIMES or NUMBERS. Two words, so the field→column name needs an entry. */
  paymentApproval: 'payment_approval',
  /* The workspace working calendar — 0216. The tenant's `work_calendar` jsonb
     ({ week, holidays }), written from the same `workspace/{id}` seam. Its own fields
     ride inside the jsonb and never pass through TIMES/NUMBERS; two words, so the
     field→column name needs an entry. Read by src/work-calendar.ts. */
  workCalendar: 'work_calendar',
  /* On hold — 0115. `heldAt` is a date (in TIMES below); the two text fields are
     multi-word, so they need an entry. `holdReason`/`holdPickup` → snake_case. */
  heldAt: 'held_at',
  holdReason: 'hold_reason',
  holdPickup: 'hold_pickup',
  /* The charter, expanded — 0101. `assumptions` and `constraints` are the same
     word either side, so (like description/sponsor/risks/vendor) they need no
     entry; the multi-word ones do. */
  scopeIn: 'scope_in',
  scopeOut: 'scope_out',
  projectManager: 'project_manager',
  payApprover: 'pay_approver',
  contractRef: 'contract_ref',
  vendorContact: 'vendor_contact',
  projectOwnerName: 'project_owner_name',
  projectOwnerPosition: 'project_owner_position',
  projectOwnerEmail: 'project_owner_email',
  ownerSignsOff: 'owner_signs_off',
  requestedOn: 'requested_on',
  signatories: 'signatories',
  signatoriesKnown: 'signatories_known',
  signedAt: 'signed_at',
  signatoryEmail: 'signatory_email',
  onBehalfBy: 'on_behalf_by',
  onBehalfReason: 'on_behalf_reason',
  /* Who covers a signer while they are away — configurable sign-offs R1, migration
     0130. `delegator`, `delegate`, `until`, `active` and `reason` are the same word
     either side, so they need no entry; the two multi-word flags do. `set_by`/
     `set_by_admin` are stamped by the guard trigger, never sent by the app, but
     they are read back, so the way-out translation needs the names. */
  setByAdmin: 'set_by_admin',
  setBy: 'set_by',
  /* `answer` and `why` are the same word either side, so they need no entry —
     COLUMNS only carries the names that differ. Listed here in a comment so the
     next person does not conclude they were forgotten. */
  /* The frozen stage order a sign-off row carries — configurable sign-offs R1,
     migration 0129. A plain `int` (a dense rank 0,1,2…), so — like `seq` — it is
     NOT in TIMES and NOT in NUMBERS: PostgREST returns an integer as a number.
     It is two words, so unlike `seq` it needs a name here or `stageSeq` would go
     across as a column that does not exist. Only `signoffs` has this column, so a
     global entry is safe — no other table has a `stageSeq` field to collide. */
  stageSeq: 'stage_seq',
  /* The frozen stage RULE a sign-off row carries — configurable sign-offs R1,
     migration 0133 (any-one / {quorum:N}; null means "everyone", the default).
     Two words, so it needs a name here for the SAME reason `stageSeq` does, or
     the raise would write a column `stageMode` that does not exist and the rule
     would never persist — which the field-column gate caught. A jsonb value, so
     it is NOT in TIMES or NUMBERS. Only `signoffs` has it, so a global entry is
     safe. */
  stageMode: 'stage_mode',
  signoffRequestedAt: 'signoff_requested_at',
  /*
   * A LINK OF THEIR OWN — #216, migration 0056. Only the hash is ever written:
   * the token is made in the browser, shown once and never stored, so a copy of
   * the table is not a set of working links.
   */
  /* The customer logo on a board's generated documents — 0063. */
  logoPath: 'logo_path',
  logoType: 'logo_type',
  linkTokenHash: 'link_token_hash',
  linkIssuedAt: 'link_issued_at',
  linkExpiresAt: 'link_expires_at',
  openedAt: 'opened_at',
  answeredVia: 'answered_via',
  /* The fingerprint of the file the request was made against, so a file
     replaced afterwards cannot be signed as though it were the one sent. */
  askedAgainst: 'asked_against',
  withdrawnAt: 'withdrawn_at',
  dismissedAt: 'dismissed_at',
  dismissedBy: 'dismissed_by',
  fileHash: 'file_hash',
  /* Whether a board's vendor may read this document — Feature B, 0196. */
  visibleToVendors: 'visible_to_vendors',
  documentId: 'document_id',
  /* ⚠️ A SIGNATURE'S OWN `sprintId` IS **NOT** HERE — see PER_TABLE. The
     column is `sprint_id`, which `sprint` on an issue already claims, and
     FIELDS is COLUMNS reversed: two fields mapping to one column means the
     later one wins the way back, so a sign-off's sprint came out named
     `sprint` and no screen could find it. #332. */
  /* Who signs on this board, as a rule rather than a list of names — #332. */
  signoffPolicy: 'signoff_policy',
  fileName: 'file_name',
  fileSize: 'file_size',
  fileType: 'file_type',
  filePath: 'file_path',
  /* The completed test-script workbook, captured beside the frozen draft (0226) —
     multi-word columns, so the seam needs them to camelCase them on the way back.
     completed_hash / completed_at are not mapped: the sign function writes them for
     the record, but the app reads the signed date off the signatory rows instead,
     and completed_at is a timestamptz that would need a TIMES entry to be ms here. */
  completedPath: 'completed_path',
  completedName: 'completed_name',
  completedType: 'completed_type',
  completedSize: 'completed_size',
  /* The pass/fail read-out parsed from the completed workbook (0226). */
  testSummary: 'test_summary',
  uploadedAt: 'uploaded_at',
  wentLiveAt: 'went_live_at',
  cutAt: 'cut_at',
  doneChecks: 'done_checks',
  /* A card's dated status log — the per-card status updates (0138). A jsonb array
     of entries ({id, at, by, text, status?, nextStep?, auto?}); like `checks` and
     `commits` it rides as JSON and is NOT in TIMES or NUMBERS. Two words, so it
     needs an entry here or the PATCH would write a `standsLog` column that does
     not exist and the log would never persist. `stands` itself is one word and
     needs none. */
  standsLog: 'stands_log',
  /* A card's Run/Change override (#435). Two words, so it needs an entry here or a
     PATCH would aim at an `investmentClass` column that does not exist and the
     override would never persist. Plain text ('change'|'run'|null), so NOT in TIMES
     or NUMBERS. */
  investmentClass: 'investment_class',
  setUpAt: 'set_up_at',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  /* When a comment's words were last changed, and by whom — the edit mark (0140).
     Two words each, so both need an entry here or a PATCH would aim at an
     `editedAt` column that does not exist. Only the comments table has these
     columns; a status-log entry carries its own `editedAt`/`editedBy` INSIDE the
     `stands_log` jsonb, which rides as JSON and never passes through this map. */
  editedAt: 'edited_at',
  editedBy: 'edited_by',
  /* A person's call-outs — track A, #388. `owner`, `kind`, `actor` and `snippet`
     are the same word either side, so they need no entry; the multi-word ones do.
     `readAt` is the only one the app ever writes. */
  boardName: 'board_name',
  targetKind: 'target_kind',
  targetId: 'target_id',
  targetRef: 'target_ref',
  targetTitle: 'target_title',
  readAt: 'read_at',
  /* When the recipient HANDLED a call-out — replied, or marked it done in My Work
     (0144). Separate from read_at; the inbox clears a handled row, the centre keeps
     it. A timestamptz, so it is also in TIMES below. */
  handledAt: 'handled_at',
  /* Who sent a document, sprint or release for sign-off — stamped at request
     time so a decision has somebody to notify (0105). */
  raisedBy: 'raised_by',
  blockerType: 'blocker_type',
  blockerTarget: 'blocker_target',
  /* The full list of blockers on a card — 0157 / #456. A jsonb array
     (BlockerEntry[]) on `issues`, read and written WHOLE, so it rides as JSON and
     is NOT in TIMES or NUMBERS. Two words, so it needs this entry or the PATCH
     would write a `blockerRefs` column that does not exist and the list would
     never persist — the same silent fault `standsLog` carries a note about. */
  blockerRefs: 'blocker_refs',
  importedFrom: 'imported_from',
  firstName: 'first_name',
  lastName: 'last_name',
  eulaVersion: 'eula_version',
  passwordSetAt: 'password_set_at',
  // When a person was deactivated — 0208. A timestamptz, so it is ALSO in TIMES
  // below; two words, so the field→column name needs this entry.
  suspendedAt: 'suspended_at',
  // #304. The column has been there since 0025 and the app had no name for it,
  // so the one fact that says "do not ask this account to choose a password"
  // never crossed the seam.
  assistant: 'is_assistant',
  // #301. A list belonging to one person, read only by them, never joined
  // against and never counted — a column, not an entity.
  savedViews: 'saved_views',
  // The Portfolio's own saved views + the id the person opens on — migration 0121,
  // the same per-person store as saved_views, for the roll-up's toolbar.
  portfolioViews: 'portfolio_views',
  portfolioDefault: 'portfolio_default',
  // A daily health snapshot per project — migration 0122, written by the
  // snapshot-pulse job and read back by the portfolio to draw a trend. `pulseOn`
  // is a plain `date` (NOT in TIMES: a day boundary must not shift a day in
  // another timezone, like target_end/starts_on); the four `*Rag` fields and
  // `onHold` are the same worst-of-three the live row derives.
  pulseOn: 'pulse_on',
  overallRag: 'overall_rag',
  scheduleRag: 'schedule_rag',
  budgetRag: 'budget_rag',
  blockersRag: 'blockers_rag',
  onHold: 'on_hold',
  // Portfolio milestones — migration 0124. `is_milestone`/`milestone_label`
  // promote a phase end; the `milestones` table adds non-phase points; the
  // `milestone_pulse` table snapshots a forecast day per milestone per day. Days
  // are plain `date`s (NOT in TIMES), like target_end/pulse_on.
  isMilestone: 'is_milestone',
  milestoneLabel: 'milestone_label',
  targetDay: 'target_day',
  forecastDay: 'forecast_day',
  // The cadence PREDICTION the daily job records alongside the plan forecast —
  // 0215. A plain `date` (NOT in TIMES), the milestone's p50 as it stood that day,
  // so the drill and pack can plot the prediction drifting out. Multi-word column,
  // so the seam needs this entry to camelCase it on the way back.
  predictedDay: 'predicted_day',
  actualDay: 'actual_day',
  milestoneKey: 'milestone_key',
  // A standalone milestone can be a PAYMENT POINT — 0155, milestone payments.
  // `billable` is the same word either side (no entry); `bill_value` is numeric,
  // so it is in NUMBERS below. Deliberately NOT a bare `value`: NUMBERS matches by
  // column name across every table, so a generic name would coerce a same-named
  // column elsewhere.
  billValue: 'bill_value',
  // The work a milestone gates, for the cadence forecast — 0215. A nullable jsonb
  // `{ phases, narrow? }` on the milestone; its own fields ride inside the jsonb and
  // never pass through TIMES/NUMBERS, the same whole-ride pattern as checklist_prompts
  // (0214). The multi-word column name needs this entry so the seam camelCases it.
  forecastTie: 'forecast_tie',
  // Taking a comment off the card writes both, and neither had a column — so
  // the removal was refused whole and the comment stayed on screen. Same
  // family as the two above: a field the app has and the seam never named.
  // #269. Null means the area is still live; a time means it was retired, which
  // is what "remove" does here — see migration 0067.
  retiredAt: 'retired_at',
  removedBy: 'removed_by',
  removedAt: 'removed_at',
  // Whether a comment is an answer the person who reported the issue may see.
  // Without a name here the flag would be dropped on the way in, and every
  // reply would come back looking like an internal note — which is the safe
  // direction to fail, but it would make the replies invisible on our own card.
  toReporter: 'for_reporter',
  // THE ENTRY A COMMENT REPLIES TO, and the address of whoever wrote it —
  // migration 0139. Multi-word, so without a name here the camelCase field would
  // be written to a column that does not exist and the reply would be refused
  // whole (the same silent fault `standsLog` and `toReporter` carry notes about).
  // `parentAuthor` is a carried hint the notify function validates; it is not the
  // card's reporter and never decides who may read anything.
  parentId: 'parent_id',
  parentAuthor: 'parent_author',
  // The first piece of sprints — see migration 0018. A status change records
  // the from and the to as fields beside the sentence, because a chart cannot
  // read "moved it from todo to done" without parsing English the app is free
  // to reword.
  fromStatus: 'from_status',
  toStatus: 'to_status',
  // Sprints — migration 0019. `sprint` on an issue is the sprint it is in, and
  // the column carries `_id` because it is a foreign key; the app says the
  // shorter thing because on a card it is the only sprint there is.
  sprint: 'sprint_id',
  addedToSprintAt: 'added_to_sprint_at',
  fromSprint: 'from_sprint',
  toSprint: 'to_sprint',
  fromSize: 'from_size',
  toSize: 'to_size',
  // A sprint's own dates. NOT in TIMES below and that is the point — they are
  // plain YYYY-MM-DD strings on both sides, because a fortnight runs from a day
  // to a day rather than from a moment to a moment. Converted, a sprint that
  // starts in Trinidad would end a day early for a reader in London.
  startsOn: 'starts_on',
  endsOn: 'ends_on',
  // A phase's target day — #377. Like a sprint's dates it is a plain `date`, so
  // it is NOT in TIMES below: a day boundary must not shift a day in another
  // timezone. `status`, `position` and `name` are the same word either side.
  targetEnd: 'target_end',
  // A phase's actual finish and a sprint's committed-to end — migration 0097.
  // `doneAt` is a timestamptz (in TIMES below); `plannedEnd` is a plain day like
  // ends_on/target_end, so it stays out of TIMES. Both written by the database.
  doneAt: 'done_at',
  plannedEnd: 'planned_end',
  // The Plan view — #377. A phase's `owner` and a step's `assignee`, `due`,
  // `status`, `name`, `position`, `signers` (jsonb) and a phase event's `actor`,
  // `kind`, `summary`, `at` are all the same word either side. Only these differ.
  // `signers` is jsonb read and written whole, so the seam never looks inside it:
  // its {person, role, signedAt} keys stay exactly as the app writes them.
  phaseId: 'phase_id',
  blockedReason: 'blocked_reason',
  addedAt: 'added_at',
  closedAt: 'closed_at',
  // `startedAt` is already mapped further up, for a session. The map is one
  // vocabulary for the whole app rather than one per table, so an issue's
  // start time needs no second entry — and adding one is a compile error,
  // which is the right way for that to be found.
  // Nine more the schema missed outright — see migration 0006. Each is a field
  // the app writes, so each needs a name here as well as a column there.
  dateInferred: 'date_inferred',
  keptByAssistant: 'kept_by_assistant',
  // Who holds the pen on the version number — see migration 0021. A decision,
  // unlike the line above, which is a fact the tool stamps.
  assistantVersions: 'assistant_versions',
  assistantMayAnswer: 'assistant_may_answer',
  // Whether this project ships to devices at all, and which of a release's
  // devices software reported rather than a person — see migration 0024.
  // `installed` needs no entry; the word is the same on both sides.
  tracksDevices: 'tracks_devices',
  installedTold: 'installed_told',
  // A release taken back — migration 0022. `pulled` needs no entry because the
  // word is the same on both sides; the four beside it do.
  pulledWhy: 'pulled_why',
  pulledAt: 'pulled_at',
  pulledBy: 'pulled_by',
  pulledBack: 'pulled_back',
  eulaAcceptedAt: 'eula_accepted_at',
  notifyEmail: 'notify_email',
  notifyWhen: 'notify_when',
  emailCallouts: 'email_callouts',
  signOutAfter: 'sign_out_after',
  startedAt: 'started_at',
  // The finish time to `started_at`'s start — migration 0096. Written by the
  // database, read by the app; it still needs a name here so the read comes
  // back as `completedAt` rather than a stray snake-case key.
  completedAt: 'completed_at',
  // A card's block clock — migration 0098. Database-written, app-read, both
  // timestamptz, so both are in TIMES below.
  blockedAt: 'blocked_at',
  unblockedAt: 'unblocked_at',
  // A card's target/due DAY — migration 0106. A `date`, not a timestamptz, so it
  // is a plain `YYYY-MM-DD` string and is deliberately NOT in TIMES below (the
  // same treatment a phase's `target_end` and a sprint's dates get — a day must
  // not be shifted by timezone conversion).
  dueDate: 'due_date',
  // A card's date BASELINES and later-PUSH counts — migration 0125. The two
  // baselines are plain `date`s like due_date/starts_on themselves, so they are
  // NOT in TIMES and never timezone-shifted; the two counts are plain integers.
  // All four are written by the database (the maintain_card_date_slip trigger)
  // and read by the app, so like completed_at they need a name here to come back
  // camel-cased rather than as stray snake_case keys.
  dueDateBaseline: 'due_date_baseline',
  startsOnBaseline: 'starts_on_baseline',
  dueDatePushes: 'due_date_pushes',
  startsOnPushes: 'starts_on_pushes',
  // How far along the work is — a hand-set 0–100 percent (migration 0199). A plain
  // `smallint`, so it needs a name here only to map the two-word field to its
  // snake_case column (and back, via the flipped FIELDS); it is not in TIMES.
  progressPct: 'progress_pct',
  // (A card's START DAY — migration 0107 — reuses `startsOn` → `starts_on`, the
  // same plain-`date` mapping a phase's start uses; it is NOT in TIMES, so unlike
  // a board's timestamptz `start_date` it is never timezone-shifted.)
  // NOT the same as startedAt, and the difference is a security control.
  // `started_at` is what "sign out everywhere" compares against and must not
  // move on a reload; `signed_in_at` moves on every fresh sign-in and is what
  // the trigger in 0068 turns into a line of History.
  signedInAt: 'signed_in_at',
  lastSeen: 'last_seen',
  // `where` is a reserved word in SQL and the app's name for a place.
  where: 'place',
  // The app calls it `who`; sessions are about a person, and `email` is what
  // every other table calls that.
  who: 'email',
  // A session's device reported in parts — migration 0149. `os`, `browser` and
  // `method` are single words that match on both sides and need no entry; only
  // these two differ from their columns. None are timestamptz, so none go in
  // TIMES. (`detail` on audit — 0149 — is likewise a same-word jsonb column.)
  deviceKind: 'device_kind',
  userAgent: 'user_agent',
  // A sign-in's network origin — migration 0150. `auditId` points at the History
  // line it belongs to; `ip`, `city` and `country` are single words that match on
  // both sides, and `at` is already in TIMES. Only this one differs.
  auditId: 'audit_id',
};

/**
 * WHERE ONE VOCABULARY IS NOT ENOUGH, and why that is not a design failure.
 *
 * A single map assumes a field name means the same thing everywhere, and twice
 * it does not:
 *
 *   `who`        is a person's address on a session, so the column is `email`.
 *                On an ACTIVITY entry it is the column's actual name — and the
 *                global rule renamed it, so every history line was refused for
 *                a column that does not exist on that table.
 *   `createdAt`  is `created_at` on an issue and on a board. On a COMMENT the
 *                column is `at`, matching activity, because both are one
 *                moment rather than a record with a life.
 *
 * Both faults had the same shape and the same silence: the store fires the
 * write with `void`, Postgres refuses the whole row, and nothing anywhere says
 * so. Posting a comment did nothing at all.
 *
 * A table's own entry wins over the general rule.
 */
const PER_TABLE: Record<string, Record<string, string>> = {
  comments: { createdAt: 'at' },
  activity: { who: 'who' },
  /*
   * THE SAME TWO MISTAKES AS ACTIVITY, FOUND SEPARATELY AND LATER.
   *
   * `who` is the column's own name here as well, and the global rule renamed it
   * to `email` — so every audit line was refused with "could not find the
   * 'email' column of 'audit'". And what the app calls the `project` an entry
   * concerns is `board_id`, which had no mapping at all.
   *
   * It is the trail that records who changed access to what, so it failing is
   * worse than it looks: the one record of a permission change, refused.
   *
   * AND TWO FIELDS WITH CONFUSINGLY SIMILAR NAMES. `projectId` is the board's
   * id and is a uuid; `project` is the short KEY that was showing at the time,
   * like "FD". The first attempt mapped `project` onto board_id, which would
   * have been worse than leaving it out — "FD" is not a uuid, so every stamped
   * line would have been refused instead of only some of them.
   */
  audit: { who: 'who', projectId: 'board_id', project: 'project_key' },
  /*
   * ── ONE COLUMN, TWO FIELDS, AND WHY THIS HAS TO BE PER TABLE — #332 ──────
   *
   * `sprint_id` is a card's sprint on `issues` and the thing a signature is
   * against on `signoffs`. The app calls them different things for good
   * reasons: on a card it is the only sprint there is, and on a signature it
   * sits beside `build` and `documentId` and has to match them.
   *
   * ⚠️ BOTH IN THE GLOBAL MAP IS A SILENT ONE-WAY FAULT, and it cost an
   * afternoon on 3 September 2026. Writing worked — `toColumns` looks up by
   * FIELD, so `sprintId` and `sprint` both find `sprint_id` and the row landed
   * perfectly. Reading is the reverse map, built by flipping COLUMNS, and a
   * duplicate value keeps the LAST key: every signoff row came back carrying
   * `sprint` instead of `sprintId`, so the sheet was in the database, correct
   * in every column, and invisible to every screen that looked for it.
   *
   * PER_TABLE is reversed per table, so the two cannot see each other.
   */
  signoffs: { sprintId: 'sprint_id' },
  /*
   * ⚠️ A REQUEST'S `email` IS ITS OWN COLUMN, AND MUST BE PINNED — #359.
   *
   * The same one-way fault as `sprint_id` above, found the hard way by walking
   * the UI: filing a request wrote `email` perfectly (`toColumns` looks up by
   * field, and `email` has no rename, so it lands in the `email` column). But
   * READING is the reverse map, and the global rule `who: 'email'` — activity
   * and comments store the actor's address in a column called `email` and call
   * the field `who` — flips to `email → who`. So every board_requests row came
   * back with its address on a `who` field nobody reads and `email` undefined.
   * Approving then called addPerson(undefined) and threw at `.trim()` before any
   * network call: the card flashed, reverted to pending, and nothing was added.
   * Pinned here so this table's own `email` survives the round trip.
   */
  board_requests: { email: 'email' },
  /*
   * A REACTION'S `issueId` IS THE COLUMN `issue_id` — migration 0143. Table-scoped
   * rather than global because no other table exposes an `issueId` field (comments
   * and activity nest inside the issue and never carry it across the seam), so a
   * global entry would be a mapping nothing else needs. `targetId`→`target_id`,
   * `at`, `emoji` and `reactor` need no entry — the first is already global and
   * the rest are the same word either side.
   */
  reactions: { issueId: 'issue_id' },
};

/**
 * FIELDS WITH NOWHERE TO GO, dropped on purpose and named so that dropping is a
 * decision rather than an accident.
 *
 * `removed` is not stored: a comment is removed when `removed_at` is set, and
 * keeping a second flag saying the same thing is how the two come to disagree.
 * It is put back on the way out from the time.
 */
const DERIVED: Record<string, Set<string>> = {
  comments: new Set(['removed']),
  /*
   * A BUILD IS ITS NUMBER ON ITS BOARD, and has no id column. The app calls it
   * "b102", which is the number with a letter in front — a label, not a key.
   * Written, it would be refused for a column that does not exist; put back on
   * the way out from the number it was always made of.
   */
  builds: new Set(['id']),
};

const FIELDS: Record<string, string> = Object.fromEntries(
  Object.entries(COLUMNS).map(([k, v]) => [v, k])
);

/**
 * TIME IS A NUMBER IN THE APP AND A TIMESTAMP HERE.
 *
 * Every date in this application is milliseconds since 1970 — that is what
 * `Date.now()` returns, it is what Firestore was given, and it is what every
 * comparison and every sort in the app is written against. Postgres columns
 * are `timestamptz`, and handed 1786070707621 it says "date/time field value
 * out of range" and refuses the write.
 *
 * So the seam converts, in both directions. Read back as a string instead, a
 * comment's `at` would sort alphabetically and compare as NaN — which is far
 * worse than the refusal, because nothing would say so.
 */
const TIMES = new Set([
  'at',
  // The RAID register's timestamptz columns — 0193. Each is ms at the app, ISO in
  // Postgres, so it converts both ways; null passes through (a risk with no review
  // scheduled, a dependency with no needed-by) rather than turning into 1970.
  'reviewed_at',
  'next_review_at',
  'validate_by_at',
  'raised_at',
  'needed_by_at',
  // When an admin decided a request to add somebody — #359.
  'decided_at',
  // #269. Null is a value here and means "still on offer" — the conversion has
  // to pass it through rather than turn it into 1970.
  'retired_at',
  'created_at',
  'updated_at',
  // When a call-out was read — track A, #388; null until it is, so the
  // conversion passes null through rather than turning it into 1970.
  'read_at',
  // When a call-out was HANDLED — replied or marked done in My Work (0144); null
  // until it is, passed through like read_at rather than turned into 1970.
  'handled_at',
  'started_at',
  // A card's finish time — migration 0096; timestamptz here, milliseconds at
  // the app, so it converts both ways like `started_at` above.
  'completed_at',
  // A card's block start and end — migration 0098. Both timestamptz, like
  // `completed_at`, so both convert ms<->ISO in each direction.
  'blocked_at',
  'unblocked_at',
  // A phase's actual finish — migration 0097. Also timestamptz; `planned_end`
  // beside it is a plain date and stays OUT of this set, like target_end.
  'done_at',
  /*
   * ⚠️ THE THIRD TIME THIS EXACT OMISSION HAS COST A FEATURE — the sign-off
   * dates below say it twice already, and this is a session's.
   *
   * `signedInAt` has a column mapping (`signed_in_at`, further up) and had no
   * entry here, so the millisecond count went across unconverted and every
   * single sign-in was refused: `date/time field value out of range:
   * "1788242237688"`. It is written ONLY on a fresh sign-in — a reload leaves
   * the field out — which is why it never failed on a refresh and why the
   * whole thing looked intermittent.
   *
   * WHAT IT COST, and none of it announces itself: `sessions` is the Devices
   * list and the thing "sign out everywhere" compares against. The row is
   * never written, so the list is empty for everybody and there is nothing for
   * a revocation to measure — a sign-out that reports success and ends nothing.
   * Found on 1 September 2026 by watching the network while walking an
   * assistant onto a board; the console said only "400" and named no field.
   */
  'signed_in_at',
  'last_seen',
  'set_up_at',
  /* A project's dates — #387. Milliseconds at the app, timestamptz here; left
     out, the count goes across unconverted and Postgres refuses the write. */
  'start_date',
  'go_live_at',
  // The real go-live moment, frozen at the go-live -> in-service handoff — 0192 /
  // #435 surface 2. Milliseconds at the app, timestamptz here, like go_live_at
  // above; left out, the count goes across unconverted and Postgres refuses the
  // write. (The planned/actual dates INSIDE delivery_snapshot ride in jsonb and
  // never pass through here, exactly as a phase's own dates inside its jsonb do.)
  'actual_go_live_at',
  // When a project was put on hold — 0115. Milliseconds at the app, timestamptz
  // here, so it converts both ways like the other project dates.
  'held_at',
  'sign_out_after',
  'removed_at',
  // When a comment's words were last changed — 0140. Milliseconds at the app,
  // timestamptz here, so the count is converted on the way in like removed_at
  // beside it; without this the raw millisecond number is sent to a timestamptz
  // column and Postgres refuses the whole row. (A status-log entry's editedAt
  // rides inside stands_log jsonb and is never a column, so it is not here.)
  'edited_at',
  'eula_accepted_at',
  'password_set_at',
  // When a person was deactivated — 0208. Milliseconds at the app, timestamptz
  // here, converted on the way in exactly like password_set_at beside it.
  'suspended_at',
  'closed_at',
  // When a phase step was added, and when a phase event happened (`at` is already
  // above) — #377. Both timestamptz, so the millisecond count is converted; a
  // step's `due` and a phase's `starts_on`/`target_end` are plain dates and stay
  // out of this set, exactly as a sprint's dates do.
  'added_at',
  'added_to_sprint_at',
  'pulled_at',
  /*
   * THE THREE DATES A SIGN-OFF HAS, and leaving them out refused the feature
   * whole. The app keeps time as milliseconds and these columns are
   * timestamptz, so the number went across unconverted and Postgres answered
   * "date/time field value out of range: 1787345923505" — which is a refusal
   * naming the millisecond count, not the field, and reads like a corrupt date
   * rather than a missing line here.
   *
   * It fails in BOTH directions from one omission: a write is not converted on
   * the way in, and a row that did get stored comes back as a string where
   * every screen expects a number. That is why the set is the seam rather than
   * the writer.
   */
  'signoff_requested_at',
  'signed_at',
  'went_live_at',
  'uploaded_at',
  'cut_at',
  /* The same three again, for the link an approver is sent — see #216. */
  'link_issued_at',
  'link_expires_at',
  'opened_at',
  'withdrawn_at',
  'dismissed_at',
  /* When a cover ends — configurable sign-offs R1, migration 0130. Milliseconds at
     the app, timestamptz here, and null is a real value ("on until turned off"),
     so the conversion passes null through rather than turning it into 1970. */
  'until',
]);

const asTimestamp = (v: unknown) =>
  typeof v === 'number' && Number.isFinite(v) ? new Date(v).toISOString() : v;

const asMillis = (v: unknown) => {
  if (typeof v === 'number' || v === null || v === undefined) return v;
  const n = Date.parse(String(v));
  return Number.isNaN(n) ? v : n;
};

/*
 * A NUMBER IN THE APP AND A `numeric` IN POSTGRES COMES BACK AS A STRING — #387.
 *
 * PostgREST returns a `numeric`/`decimal` column as a STRING to keep precision
 * a JS number cannot promise. So a budget written as 48000 reads back as
 * "48000", and every screen that does maths on it — a variance, a portfolio
 * roll-up — gets string concatenation or NaN instead. This is the same shape
 * of trap as the timestamps above: the column type is not the app's type, so
 * the seam converts. On the way IN a JS number is fine as-is; only the way out
 * needs turning back.
 */
const NUMBERS = new Set(['budget', 'capex', 'opex', 'actual_spend', 'forecast', 'contract_value', 'bill_value']);

const asNumber = (v: unknown) => {
  if (typeof v === 'number' || v === null || v === undefined) return v;
  const n = Number(v);
  return typeof v === 'string' && v.trim() !== '' && !Number.isNaN(n) ? n : v;
};

export const toColumns = (
  rec: Record<string, unknown>,
  table?: string
): Record<string, unknown> => {
  const own = (table && PER_TABLE[table]) || {};
  const drop = (table && DERIVED[table]) || new Set<string>();
  return Object.fromEntries(
    Object.entries(rec)
      .filter(([k]) => !drop.has(k))
      .map(([k, v]) => {
        const col = own[k] ?? COLUMNS[k] ?? k;
        return [col, TIMES.has(col) ? asTimestamp(v) : v];
      })
  );
};

export const toFields = (
  row: Record<string, unknown>,
  table?: string
): Record<string, unknown> => {
  const own = (table && PER_TABLE[table]) || {};
  // Read the table's own renames backwards, so a round trip returns what went
  // in — the property this is actually tested on.
  const back = Object.fromEntries(Object.entries(own).map(([k, v]) => [v, k]));
  const out = Object.fromEntries(
    Object.entries(row).map(([k, v]) => [
      back[k] ?? FIELDS[k] ?? k,
      TIMES.has(k) ? asMillis(v) : NUMBERS.has(k) ? asNumber(v) : v,
    ])
  );
  // Put the derived flag back from the thing it is derived FROM.
  if (table === 'comments') out.removed = row.removed_at != null;
  return out;
};
